// [BD feat] Header — bd-header.js
// 1. Smooth scroll for in-page anchors marked data-bd-scroll (dev note: "GET IN EARLY" → anchor to the
//    sign-up form, smooth). prefers-reduced-motion → instant jump.
// 2. Teaser state: while the hero teaser section is in view the fixed header shows only the logo
//    pill (Figma 2976:3869 / 2881:2913); once the teaser is scrolled past, "GET IN EARLY" appears
//    (page frame 2978:4610). No teaser section → the pill is always visible.
// 3. Announcement bar: when the client enables it, the fixed header sits below it until it scrolls out.
(function () {
  'use strict';

  var header = document.querySelector('.bd-header');
  if (!header) return;

  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // [BD feat] Homepage variant (data-bd-header-home, TWLV-home) — the password-page media states below
  // (teaser / light) never run on it; it has its own: over-media fill, hide-on-scroll-down, mobile menu
  // layer and the live cart count.
  var home = header.hasAttribute('data-bd-header-home');
  var menuOpen = false;

  function initHome() {
    var root = document.documentElement;
    var toggle = header.querySelector('[data-bd-menu-toggle]');
    var drawer = header.querySelector('[data-bd-menu-drawer]');

    // --- over-media state: pills #151515cc while the bar sits over the hero media (3540:10857) ---
    var media = document.querySelectorAll('.bd-hero-home, [data-bd-header-media]');
    var overMedia = {};
    function applyMedia() {
      var on = false;
      for (var k in overMedia) if (overMedia[k]) on = true;
      header.classList.toggle('bd-header--over-media', on);
    }
    if ('IntersectionObserver' in window && media.length) {
      var barBottom = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry, i) {
          var key = entry.target.id || ('m' + i);
          overMedia[key] = entry.isIntersecting && entry.boundingClientRect.bottom > barBottom;
        });
        applyMedia();
      }, { rootMargin: '-' + barBottom + 'px 0px 0px 0px', threshold: 0 });
      media.forEach(function (el) { io.observe(el); });
    } else if (!media.length) {
      header.classList.remove('bd-header--over-media');
    }

    // --- hide on scroll down, show on scroll up (dev note); "always sticky" keeps it visible ---
    var alwaysSticky = header.classList.contains('bd-header--always-sticky');
    // Pinned scenes (your-new-day: a sticky, viewport-filling stage with its label at the top — page review R001):
    // while one is pinned the bar stays hidden, because scrolling up inside it would put the pills over the label.
    // It comes back as usual once the scene unpins (scroll up past its top, or on past its end).
    var scenes = document.querySelectorAll('[data-bd-header-avoid]');
    function scenePinned() {
      for (var i = 0; i < scenes.length; i++) {
        var track = scenes[i];
        var stage = track.querySelector('[data-bd-header-avoid-stage]') || track.firstElementChild || track;
        var r = track.getBoundingClientRect();
        if (r.top <= 0 && r.bottom >= (stage.offsetHeight || 0) - 1) return true;
      }
      return false;
    }
    // "always sticky" never hides the bar → tell the page (html.bd-header-always-sticky) so a pinned scene can keep
    // its label clear of the pills itself (bd-routine.css); --bd-header-clear = the pills' bottom edge.
    var bar = header.querySelector('.bd-header__bar');
    function syncClear() {
      var clear = bar ? Math.round(bar.offsetTop + bar.offsetHeight) : header.offsetHeight;
      root.style.setProperty('--bd-header-clear', clear + 'px');
    }
    root.classList.toggle('bd-header-always-sticky', alwaysSticky);
    syncClear();
    window.addEventListener('resize', syncClear);
    var lastY = window.scrollY;
    var scrollTick = false;
    function onHomeScroll() {
      scrollTick = false;
      var y = window.scrollY;
      // never hide while the menu or the legal layer (html.bd-legal-open, bd-footer.js) is open — the pills close it
      if (alwaysSticky || menuOpen || root.classList.contains('bd-legal-open')) { header.classList.remove('bd-header--hidden'); lastY = y; return; }
      if (scenes.length && scenePinned()) { header.classList.add('bd-header--hidden'); lastY = y; return; }
      var barH = header.offsetHeight || 0;
      if (y > lastY + 4 && y > barH) header.classList.add('bd-header--hidden');
      else if (y < lastY - 4 || y <= barH) header.classList.remove('bd-header--hidden');
      lastY = y;
    }
    // a restored scroll position can land inside a pinned scene before any scroll event
    if (scenes.length) window.requestAnimationFrame(onHomeScroll);
    window.addEventListener('scroll', function () {
      if (scrollTick) return;
      scrollTick = true;
      window.requestAnimationFrame(onHomeScroll);
    }, { passive: true });
    // A hidden header must never hold keyboard focus (WCAG 2.4.7 / 2.4.11): any focus inside reveals it.
    header.addEventListener('focusin', function () {
      header.classList.remove('bd-header--hidden');
      lastY = window.scrollY;
    });

    // --- mobile menu layer: the nav pill itself, shown as a full-screen list below 60em (one nav in the DOM).
    //     While open: everything outside the header section is inert (page behind cannot be reached, no
    //     background scroll), focus cycles inside the header, Esc / a link / the pill close it and the page
    //     returns to the scroll position it had when the menu opened. ---
    var menuY = 0;
    var inerted = [];
    var headerSection = header.closest('.shopify-section') || header;
    function focusables() {
      return Array.prototype.filter.call(header.querySelectorAll('a[href], button:not([disabled])'), function (el) {
        var cs = getComputedStyle(el);
        return cs.display !== 'none' && cs.visibility !== 'hidden' && (el.offsetParent !== null || cs.position === 'fixed' || el.closest('.bd-header__nav'));
      });
    }
    function setMenu(open) {
      if (!toggle || !drawer) return;
      menuOpen = open;
      toggle.setAttribute('aria-expanded', String(open));
      toggle.setAttribute('aria-label', open ? (toggle.dataset.bdLabelClose || '') : (toggle.dataset.bdLabelOpen || ''));
      header.classList.toggle('bd-header--menu-open', open);
      root.classList.toggle('bd-menu-open', open);
      if (open) {
        menuY = window.scrollY;
        header.classList.remove('bd-header--hidden');
        drawer.setAttribute('role', 'dialog');
        drawer.setAttribute('aria-modal', 'true');
        inerted = Array.prototype.filter.call(document.body.children, function (el) {
          return el !== headerSection && !el.contains(header) && !el.inert && el.tagName !== 'SCRIPT';
        });
        inerted.forEach(function (el) { el.inert = true; });
        var first = drawer.querySelector('a');
        if (first) first.focus({ preventScroll: true });
      } else {
        drawer.removeAttribute('role');
        drawer.removeAttribute('aria-modal');
        inerted.forEach(function (el) { el.inert = false; });
        inerted = [];
        if (window.scrollY !== menuY) window.scrollTo({ top: menuY, behavior: 'instant' });
        lastY = menuY;
        toggle.focus({ preventScroll: true });
      }
    }
    if (toggle && drawer) {
      toggle.addEventListener('click', function () { setMenu(!menuOpen); });
      document.addEventListener('keydown', function (e) {
        if (!menuOpen) return;
        if (e.key === 'Escape') { setMenu(false); return; }
        if (e.key !== 'Tab') return;
        var items = focusables();
        if (!items.length) return;
        var first = items[0], last = items[items.length - 1];
        if (e.shiftKey && (document.activeElement === first || !header.contains(document.activeElement))) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && (document.activeElement === last || !header.contains(document.activeElement))) { e.preventDefault(); first.focus(); }
      });
      drawer.addEventListener('click', function (e) {
        var a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
        if (a) setMenu(false);
      });
      var desktopMQ = window.matchMedia ? window.matchMedia('(min-width: 60em)') : null;
      if (desktopMQ && desktopMQ.addEventListener) desktopMQ.addEventListener('change', function (e) { if (e.matches && menuOpen) setMenu(false); });
    }

    // --- live cart count: Dawn publishes PUB_SUB_EVENTS.cartUpdate after every cart change ---
    var countEls = header.querySelectorAll('[data-bd-cart-count]');
    var cartUrl = header.getAttribute('data-bd-cart-url') || '/cart';
    function setCount(n) { countEls.forEach(function (el) { el.textContent = n; }); }
    function refreshCount(event) {
      if (!countEls.length) return;
      // cart-items / cart-drawer publish the full cart JSON (item_count); product-form publishes the added line only
      if (event && event.cartData && typeof event.cartData.item_count === 'number') { setCount(event.cartData.item_count); return; }
      fetch(cartUrl + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (cart) {
          if (!cart || typeof cart.item_count !== 'number') return;
          setCount(cart.item_count);
        })
        .catch(function () {});
    }
    // Dawn declares `const PUB_SUB_EVENTS` (constants.js) — a lexical global, never a window property — and
    // `function subscribe` (pubsub.js); both deferred scripts run before this one (document order).
    /* global PUB_SUB_EVENTS, subscribe */
    if (typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate && typeof subscribe === 'function') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, refreshCount);
    }
    window.addEventListener('pageshow', function (e) { if (e.persisted) refreshCount(); });
  }

  if (home) initHome();
  // [/BD feat]

  // Sections above the target can change height while the page scrolls past them (the performance-system
  // accordion is scroll-driven), which leaves the target short of y=0 once the smooth scroll ends. Once
  // the scroll has settled, snap the remaining difference instantly (a few passes, then give up).
  // A section's top padding is the light gap between sections; the anchor lands where the section's
  // visual (e.g. the signup background image) starts, so no white band shows above it.
  function landingTop(target) {
    return target.getBoundingClientRect().top + (parseFloat(getComputedStyle(target).paddingTop) || 0);
  }

  function settleOn(target, passes) {
    var lastY = -1;
    var still = 0;
    (function poll() {
      var y = window.scrollY;
      still = y === lastY ? still + 1 : 0;
      lastY = y;
      if (still < 3) return window.requestAnimationFrame(poll);
      var off = landingTop(target);
      if (Math.abs(off) > 1 && passes > 0) {
        window.scrollBy(0, off);
        settleOn(target, passes - 1);
      }
    })();
  }

  // --- smooth anchor scroll (header pills, [data-bd-scroll] links and in-page links such as the FAQ's
  //     "GET FIRST ACCESS." richtext link) ---
  document.addEventListener('click', function (e) {
    var link = e.target && e.target.closest ? e.target.closest('a[data-bd-scroll], a[href^="#"]') : null;
    if (!link) return;
    var href = link.getAttribute('href') || '';
    if (href.charAt(0) !== '#' || href.length < 2) return;
    var target = document.getElementById(href.slice(1));
    // Signup success state: the form is hidden, so the pills lead to the success message instead
    // (coordinator decision — flagged to the designer). Also covers any hidden anchor target.
    var retargeted = false;
    var focusTarget = target;
    if (document.body.classList.contains('bd-signup-success-active') || (target && target.offsetParent === null && getComputedStyle(target).position !== 'fixed')) {
      var successSection = document.querySelector('[data-bd-signup-success-section]');
      if (successSection && successSection.offsetParent !== null) {
        target = successSection; // the section top lands at y=0
        focusTarget = successSection.querySelector('[data-bd-signup-success-heading]') || successSection;
        retargeted = true;
      }
    }
    if (!target) return;
    e.preventDefault();
    // The target section's top edge lands exactly at the viewport top; the floating pills overlay it (design).
    // While the smooth scroll runs, html.bd-anchor-scrolling tells scroll-driven sections (performance-system
    // accordion) to hold their state, so nothing above the target changes height mid-flight.
    var root = document.documentElement;
    root.classList.add('bd-anchor-scrolling');
    // bd-images.js: promote the target section's images (and those on the way) before the scroll starts
    document.dispatchEvent(new CustomEvent('bd:anchor', { detail: { target: target } }));
    var release = function () { root.classList.remove('bd-anchor-scrolling'); window.removeEventListener('scrollend', release); };
    if (reduced) { window.setTimeout(release, 0); }
    else { window.addEventListener('scrollend', release); window.setTimeout(release, 1500); }
    window.scrollTo({ top: window.scrollY + landingTop(target), behavior: reduced ? 'auto' : 'smooth' });
    settleOn(target, 3);
    if (!retargeted && history.pushState) history.pushState(null, '', href);
    if (focusTarget && typeof focusTarget.focus === 'function') {
      if (!focusTarget.hasAttribute('tabindex')) focusTarget.setAttribute('tabindex', '-1');
      focusTarget.focus({ preventScroll: true });
    }
  });

  // --- media states (IntersectionObserver on the teaser + hero sections) ---
  //   bd-header--teaser : the teaser still covers the bar → logo only
  //   bd-header--light  : neither dark media section covers the bar (header sits over the light
  //                       #fafafa sections) → mobile pills switch to the desktop fill for contrast
  var mediaIO = null;
  var covering = { teaser: false, hero: false };
  function applyState() {
    header.classList.toggle('bd-header--teaser', covering.teaser);
    header.classList.toggle('bd-header--light', !covering.teaser && !covering.hero);
  }
  function watchTeaser() {
    if (home) return; // [BD feat] homepage variant: its own media state (initHome) [/BD feat]
    if (mediaIO) { mediaIO.disconnect(); mediaIO = null; }
    var teaser = document.querySelector('.bd-hero-teaser');
    var hero = document.querySelector('.bd-hero');
    var bar = header.querySelector('.bd-header__bar');
    covering = { teaser: false, hero: false };
    if (!bar || !('IntersectionObserver' in window) || (!teaser && !hero)) {
      applyState();
      return;
    }
    // The root is the viewport minus the header band: a section "covers" the header while its bottom
    // edge is still below the bar's bottom edge.
    var barBottom = Math.max(0, Math.round(bar.getBoundingClientRect().bottom));
    mediaIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var on = entry.isIntersecting && entry.boundingClientRect.bottom > barBottom;
        if (entry.target === teaser) covering.teaser = on;
        if (entry.target === hero) covering.hero = on;
      });
      applyState();
    }, { rootMargin: '-' + barBottom + 'px 0px 0px 0px', threshold: 0 });
    if (teaser) mediaIO.observe(teaser);
    if (hero) mediaIO.observe(hero);
  }

  // --- announcement bar offset (only when the bar is rendered) ---
  var announcement = document.querySelector('.bd-announcement-bar');
  var ticking = false;
  function syncOffset() {
    ticking = false;
    if (!announcement) return;
    var bottom = Math.max(0, Math.round(announcement.getBoundingClientRect().bottom));
    header.style.setProperty('--bd-header-offset', bottom + 'px');
  }
  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(syncOffset);
  }
  if (announcement) {
    window.addEventListener('scroll', onScroll, { passive: true });
    syncOffset();
  }

  var resizeTimer = null;
  window.addEventListener('resize', function () {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(function () { watchTeaser(); syncOffset(); }, 150);
  });

  watchTeaser();

  // Shopify theme editor: sections re-render without a page load.
  document.addEventListener('shopify:section:load', function () {
    header = document.querySelector('.bd-header') || header;
    announcement = document.querySelector('.bd-announcement-bar');
    watchTeaser();
    syncOffset();
  });
})();
// [BD feat] Header
